// Colors
import { DropdownInputOption } from '@/features/editor/widgets/custom-widget/inputs/shared/dropdown';

export const COLOR_FIELDS = [
  `color`,
  `background-color`,
  `background`,
  `border-color`,
  `outline-color`,
  `text-decoration-color`,
];

// Numeric + Unit (px, em, %, etc.)
export const NUMERIC_UNIT_FIELDS = [
  `width`,
  `min-width`,
  `max-width`,
  `height`,
  `min-height`,
  `max-height`,
  `font-size`,
  `letter-spacing`,
  `word-spacing`,
  `border-width`,
  `outline-width`,
  `top`,
  `right`,
  `bottom`,
  `left`,
  `flex-basis`,
  `gap`,
  `row-gap`,
  `column-gap`,
  `margin-top`,
  `margin-right`,
  `margin-bottom`,
  `margin-left`,
  `padding-top`,
  `padding-right`,
  `padding-bottom`,
  `padding-left`,
  `border-radius`,
];

// Unitless numeric fields
export const NUMERIC_FIELDS = [
  `opacity`,
  `z-index`,
  `flex-grow`,
  `flex-shrink`,
  `order`,
  `line-height`,
];

export const SPACING_FIELDS = [`margin`, `padding`, `inset`];

export const ENUM_FIELDS = [
  `display`,
  `position`,
  `flex-direction`,
  `flex-wrap`,
  `justify-content`,
  `align-items`,
  `align-content`,
  `align-self`,
  `text-align`,
  `overflow`,
  `overflow-x`,
  `overflow-y`,
  `visibility`,
  `white-space`,
  `word-break`,
  `text-overflow`,
  `font-style`,
  `font-weight`,
  `text-transform`,
  `text-decoration`,
  `box-sizing`,
  `object-fit`,
  `object-position`,
  `background-repeat`,
  `background-position`,
  `background-size`,
  `list-style-type`,
  `list-style-position`,
  `cursor`,
  `user-select`,
  `pointer-events`,
];

export const ALL_FIELDS_OPTIONS = [
  ...COLOR_FIELDS,
  ...NUMERIC_FIELDS,
  ...NUMERIC_UNIT_FIELDS,
  ...SPACING_FIELDS,
  ...ENUM_FIELDS,
  `border`,
  `box-shadow`,
  `text-shadow`,
  `font-family`,
].map((field) => ({
  label: toCssDeclarationName(field),
  value: field,
}));

export const ALLOWED_UNITS = [
  // Absolute length units
  `px`, // Pixels
  `pt`, // Points
  `pc`, // Picas
  `in`, // Inches
  `cm`, // Centimeters
  `mm`, // Millimeters
  `q`, // Quarter-millimeters

  // Font-relative units
  `em`, // Relative to parent font-size
  `rem`, // Relative to root font-size
  `ex`, // x-height of the current font
  `ch`, // Width of the "0" (zero) glyph
  `ic`, // Width of a CJK water ideograph

  // Viewport-relative units
  `vw`, // 1% of viewport width
  `vh`, // 1% of viewport height
  `vmin`, // Smaller of vw or vh
  `vmax`, // Larger of vw or vh
  `svw`,
  `svh`,
  `lvw`,
  `lvh`,
  `dvw`,
  `dvh`, // Safe/large/dynamic viewport units

  // Container-relative units (CSS Container Queries)
  `cqw`, // 1% of container's width
  `cqh`, // 1% of container's height
  `cqi`, // 1% of container's inline size
  `cqb`, // 1% of container's block size
  `cqmin`, // Smaller of cqi/cqb
  `cqmax`, // Larger of cqi/cqb

  // Percentage
  `%`, // Relative to parent or context
];

export const ENUM_FIELD_VALUES: Record<string, DropdownInputOption[]> = {
  display: [
    { label: `Block`, value: `block` },
    { label: `Inline`, value: `inline` },
    { label: `Inline Block`, value: `inline-block` },
    { label: `Flex`, value: `flex` },
    { label: `Grid`, value: `grid` },
    { label: `Inline Flex`, value: `inline-flex` },
    { label: `None`, value: `none` },
  ],
  position: [
    { label: `Static`, value: `static` },
    { label: `Relative`, value: `relative` },
    { label: `Absolute`, value: `absolute` },
    { label: `Fixed`, value: `fixed` },
    { label: `Sticky`, value: `sticky` },
  ],
  'flex-direction': [
    { label: `Row`, value: `row` },
    { label: `Row Reverse`, value: `row-reverse` },
    { label: `Column`, value: `column` },
    { label: `Column Reverse`, value: `column-reverse` },
  ],
  'flex-wrap': [
    { label: `No Wrap`, value: `nowrap` },
    { label: `Wrap`, value: `wrap` },
    { label: `Wrap Reverse`, value: `wrap-reverse` },
  ],
  'justify-content': [
    { label: `Flex Start`, value: `flex-start` },
    { label: `Flex End`, value: `flex-end` },
    { label: `Center`, value: `center` },
    { label: `Space Between`, value: `space-between` },
    { label: `Space Around`, value: `space-around` },
    { label: `Space Evenly`, value: `space-evenly` },
  ],
  'align-items': [
    { label: `Stretch`, value: `stretch` },
    { label: `Flex Start`, value: `flex-start` },
    { label: `Flex End`, value: `flex-end` },
    { label: `Center`, value: `center` },
    { label: `Baseline`, value: `baseline` },
  ],
  'align-content': [
    { label: `Stretch`, value: `stretch` },
    { label: `Center`, value: `center` },
    { label: `Flex Start`, value: `flex-start` },
    { label: `Flex End`, value: `flex-end` },
    { label: `Space Between`, value: `space-between` },
    { label: `Space Around`, value: `space-around` },
  ],
  'align-self': [
    { label: `Auto`, value: `auto` },
    { label: `Flex Start`, value: `flex-start` },
    { label: `Flex End`, value: `flex-end` },
    { label: `Center`, value: `center` },
    { label: `Baseline`, value: `baseline` },
    { label: `Stretch`, value: `stretch` },
  ],
  'text-align': [
    { label: `Left`, value: `left` },
    { label: `Right`, value: `right` },
    { label: `Center`, value: `center` },
    { label: `Justify`, value: `justify` },
  ],
  overflow: [
    { label: `Visible`, value: `visible` },
    { label: `Hidden`, value: `hidden` },
    { label: `Scroll`, value: `scroll` },
    { label: `Auto`, value: `auto` },
    { label: `Clip`, value: `clip` },
  ],
  'overflow-x': [
    { label: `Visible`, value: `visible` },
    { label: `Hidden`, value: `hidden` },
    { label: `Scroll`, value: `scroll` },
    { label: `Auto`, value: `auto` },
    { label: `Clip`, value: `clip` },
  ],
  'overflow-y': [
    { label: `Visible`, value: `visible` },
    { label: `Hidden`, value: `hidden` },
    { label: `Scroll`, value: `scroll` },
    { label: `Auto`, value: `auto` },
    { label: `Clip`, value: `clip` },
  ],
  visibility: [
    { label: `Visible`, value: `visible` },
    { label: `Hidden`, value: `hidden` },
  ],
  'white-space': [
    { label: `Normal`, value: `normal` },
    { label: `Nowrap`, value: `nowrap` },
    { label: `Pre`, value: `pre` },
    { label: `Pre-wrap`, value: `pre-wrap` },
    { label: `Pre-line`, value: `pre-line` },
    { label: `Break-spaces`, value: `break-spaces` },
  ],
  'word-break': [
    { label: `Normal`, value: `normal` },
    { label: `Break-all`, value: `break-all` },
    { label: `Keep-all`, value: `keep-all` },
    { label: `Break-word`, value: `break-word` },
  ],
  'text-overflow': [
    { label: `Clip`, value: `clip` },
    { label: `Ellipsis`, value: `ellipsis` },
  ],
  'font-style': [
    { label: `Normal`, value: `normal` },
    { label: `Italic`, value: `italic` },
    { label: `Oblique`, value: `oblique` },
  ],
  'font-weight': [
    { label: `Normal`, value: `normal` },
    { label: `Bold`, value: `bold` },
    { label: `Bolder`, value: `bolder` },
    { label: `Lighter`, value: `lighter` },
    { label: `100`, value: `100` },
    { label: `200`, value: `200` },
    { label: `300`, value: `300` },
    { label: `400`, value: `400` },
    { label: `500`, value: `500` },
    { label: `600`, value: `600` },
    { label: `700`, value: `700` },
    { label: `800`, value: `800` },
    { label: `900`, value: `900` },
  ],
  'font-family': [
    { label: `Serif`, value: `serif` },
    { label: `Sans-serif`, value: `sans-serif` },
    { label: `Monospace`, value: `monospace` },
    { label: `Cursive`, value: `cursive` },
    { label: `Fantasy`, value: `fantasy` },
    { label: `System UI`, value: `system-ui` },
    { label: `Arial`, value: `Arial` },
    { label: `Helvetica`, value: `Helvetica` },
    { label: `Times New Roman`, value: `"Times New Roman"` },
    { label: `Courier New`, value: `"Courier New"` },
  ],
  'text-transform': [
    { label: `None`, value: `none` },
    { label: `Capitalize`, value: `capitalize` },
    { label: `Uppercase`, value: `uppercase` },
    { label: `Lowercase`, value: `lowercase` },
  ],
  'text-decoration': [
    { label: `None`, value: `none` },
    { label: `Underline`, value: `underline` },
    { label: `Overline`, value: `overline` },
    { label: `Line Through`, value: `line-through` },
  ],
  'box-sizing': [
    { label: `Content-box`, value: `content-box` },
    { label: `Border-box`, value: `border-box` },
  ],
  'object-fit': [
    { label: `Fill`, value: `fill` },
    { label: `Contain`, value: `contain` },
    { label: `Cover`, value: `cover` },
    { label: `None`, value: `none` },
    { label: `Scale-down`, value: `scale-down` },
  ],
  'object-position': [
    { label: `Top Left`, value: `top left` },
    { label: `Top Center`, value: `top center` },
    { label: `Top Right`, value: `top right` },
    { label: `Center Left`, value: `center left` },
    { label: `Center Center`, value: `center center` },
    { label: `Center Right`, value: `center right` },
    { label: `Bottom Left`, value: `bottom left` },
    { label: `Bottom Center`, value: `bottom center` },
    { label: `Bottom Right`, value: `bottom right` },
  ],
  'background-repeat': [
    { label: `Repeat`, value: `repeat` },
    { label: `No-repeat`, value: `no-repeat` },
    { label: `Repeat-x`, value: `repeat-x` },
    { label: `Repeat-y`, value: `repeat-y` },
    { label: `Round`, value: `round` },
    { label: `Space`, value: `space` },
  ],
  'background-position': [
    { label: `Left Top`, value: `left top` },
    { label: `Left Center`, value: `left center` },
    { label: `Left Bottom`, value: `left bottom` },
    { label: `Center Top`, value: `center top` },
    { label: `Center Center`, value: `center center` },
    { label: `Center Bottom`, value: `center bottom` },
    { label: `Right Top`, value: `right top` },
    { label: `Right Center`, value: `right center` },
    { label: `Right Bottom`, value: `right bottom` },
  ],
  'background-size': [
    { label: `Auto`, value: `auto` },
    { label: `Cover`, value: `cover` },
    { label: `Contain`, value: `contain` },
  ],
  'list-style-type': [
    { label: `Disc`, value: `disc` },
    { label: `Circle`, value: `circle` },
    { label: `Square`, value: `square` },
    { label: `Decimal`, value: `decimal` },
    { label: `Decimal-leading-zero`, value: `decimal-leading-zero` },
    { label: `Lower-roman`, value: `lower-roman` },
    { label: `Upper-roman`, value: `upper-roman` },
    { label: `None`, value: `none` },
  ],
  'list-style-position': [
    { label: `Inside`, value: `inside` },
    { label: `Outside`, value: `outside` },
  ],
  cursor: [
    { label: `Default`, value: `default` },
    { label: `Pointer`, value: `pointer` },
    { label: `Text`, value: `text` },
    { label: `Move`, value: `move` },
    { label: `Not-allowed`, value: `not-allowed` },
    { label: `Wait`, value: `wait` },
    { label: `Crosshair`, value: `crosshair` },
    { label: `Help`, value: `help` },
    { label: `Progress`, value: `progress` },
    { label: `Grab`, value: `grab` },
    { label: `Grabbing`, value: `grabbing` },
  ],
  'user-select': [
    { label: `Auto`, value: `auto` },
    { label: `Text`, value: `text` },
    { label: `None`, value: `none` },
    { label: `Contain`, value: `contain` },
    { label: `All`, value: `all` },
  ],
  'pointer-events': [
    { label: `Auto`, value: `auto` },
    { label: `None`, value: `none` },
    { label: `All`, value: `all` },
  ],
};

export function defaultCssDeclarationValue(name: string): string {
  if (COLOR_FIELDS.includes(name)) {
    return `#000000`;
  }
  if (NUMERIC_UNIT_FIELDS.includes(name)) {
    return `0px`;
  }
  if (NUMERIC_FIELDS.includes(name)) {
    return `0`;
  }
  if (ENUM_FIELDS.includes(name)) {
    return ENUM_FIELD_VALUES[name]?.[0]?.value || `inherit`;
  }
  if (SPACING_FIELDS.includes(name)) {
    return `0px 0px 0px 0px`;
  }
  return `none`;
}

export function toCssDeclarationName(name: string): string {
  const out = name
    .split(`-`)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(` `);

  if (out === `Color`) {
    return `Foreground Color`;
  }
  return out;
}
